// src/pages/Event/EventFormPage.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import toast from "react-hot-toast";
import {
  Plus,
  Minus,
  Trash2,
  Loader2,
  Search,
  Calendar,
  Clock,
  DollarSign,
  Percent,
  User2,
  Building2,
  Save,
  Sparkles,
  ArrowLeft,
  X,
  Briefcase,
  Wallet,
  ChevronRight,
  ChevronLeft,
  UtensilsCrossed,
  Mail,
  Phone,
  Users,
} from "lucide-react";

/* shadcn/ui */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

/* ───────── field styles ───────── */
const calmField =
  "bg-white text-slate-950 placeholder:text-slate-500 border border-slate-300 rounded-2xl shadow-sm " +
  "focus-visible:ring-2 focus-visible:ring-sky-100 focus-visible:border-sky-500 focus-visible:outline-none";

const calmArea = `${calmField} min-h-[110px]`;
const calmSelectTrigger =
  "bg-white text-slate-950 border border-slate-300 rounded-2xl shadow-sm focus-visible:ring-2 focus-visible:ring-sky-100 focus-visible:border-sky-500";

const strongLabel = "text-sm font-medium text-slate-800";

/* ───────── helpers ───────── */
const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const clamp = (v, min = 0, max = 1e9) => Math.max(min, Math.min(max, num(v)));

const toPositiveIntOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number.parseInt(String(v), 10);
  return Number.isInteger(n) && n > 0 ? n : null;
};

function normalizeMenuId(m) {
  return toPositiveIntOrNull(m?.menu_item_id ?? m?.item_id ?? m?.id);
}

function toMoneyInputString(v) {
  return String(v ?? "").replace(/[^0-9.\-]/g, "");
}

const currency = (v) =>
  (num(v) || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "GBP",
  });

function buildPlannedItems(lines) {
  if (!Array.isArray(lines) || !lines.length) return [];

  return lines
    .map((l) => {
      const menu_item_id = toPositiveIntOrNull(l.item_id);
      const servings = Math.max(0, Number(l.qty || 0));
      const event_sale_price = num(l.unit_price, 0);

      if (!menu_item_id) return null;
      if (!Number.isFinite(servings) || servings < 0) return null;

      return {
        menu_item_id,
        servings,
        event_sale_price,
      };
    })
    .filter(Boolean);
}

function buildStaffingCostLines(lines) {
  if (!Array.isArray(lines) || !lines.length) return [];

  return lines
    .map((l) => {
      const cost_centre_id = toPositiveIntOrNull(l.cost_centre_id);
      const amount = num(l.amount, 0);
      const notes = String(l.notes ?? "").trim();

      if (!cost_centre_id) return null;
      if (!Number.isFinite(amount) || amount < 0) return null;

      return {
        cost_centre_id,
        amount,
        notes: notes || null,
      };
    })
    .filter(Boolean);
}

function asRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.reports)) return payload.reports;
  if (Array.isArray(payload?.forecast_rows)) return payload.forecast_rows;
  if (Array.isArray(payload?.actual_rows)) return payload.actual_rows;
  if (Array.isArray(payload?.cost_centres)) return payload.cost_centres;
  if (Array.isArray(payload?.costCentres)) return payload.costCentres;
  if (Array.isArray(payload?.planners)) return payload.planners;
  if (Array.isArray(payload?.clients)) return payload.clients;
  return [];
}

function extractForecastStaffingRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload?.forecast_rows)) return payload.forecast_rows;
  if (Array.isArray(payload?.forecastRows)) return payload.forecastRows;
  if (Array.isArray(payload?.data?.forecast_rows)) return payload.data.forecast_rows;
  if (Array.isArray(payload?.data?.forecastRows)) return payload.data.forecastRows;
  return [];
}

function extractCostCentreRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload?.cost_centres)) return payload.cost_centres;
  if (Array.isArray(payload?.costCentres)) return payload.costCentres;
  if (Array.isArray(payload?.data?.cost_centres)) return payload.data.cost_centres;
  if (Array.isArray(payload?.data?.costCentres)) return payload.data.costCentres;
  return [];
}

function normalizeCategoryToType(category) {
  const c = String(category || "").toLowerCase();
  if (c.includes("drink") || c.includes("beverage") || c.includes("bar")) return "drink";
  return "food";
}

function normalizeEventResponse(data) {
  return (
    data?.event ||
    data?.details?.event ||
    data?.data?.event ||
    data?.data?.details?.event ||
    data?.details ||
    data?.data ||
    data ||
    {}
  );
}

function isoDateOnly(v) {
  if (!v) return "";
  const s = String(v);
  return s.includes("T") ? s.split("T")[0] : s.slice(0, 10);
}

function timeHHMM(v) {
  if (!v) return "";
  const s = String(v);
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5);
  if (s.includes("T")) return s.split("T")[1]?.slice(0, 5) || "";
  return s.slice(0, 5);
}

function isValidEmail(email) {
  const s = String(email || "").trim();
  if (!s) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function formatClientAddress(client) {
  if (!client || typeof client !== "object") return "";
  return [
    client.address_line_1,
    client.address_line_2,
    client.city,
    client.county,
    client.postcode,
    client.country,
  ]
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .join(", ");
}

function normalizePlannerRow(p) {
  const planner_id = toPositiveIntOrNull(p?.planner_id ?? p?.id);
  const label =
    p?.planner_name ||
    p?.full_name ||
    p?.name ||
    p?.email ||
    (planner_id ? `Planner ${planner_id}` : "Unnamed planner");

  return planner_id
    ? {
        planner_id,
        label,
        raw: p,
      }
    : null;
}

function normalizeCostCentreRow(c) {
  const cost_centre_id = toPositiveIntOrNull(c?.cost_centre_id ?? c?.id);
  if (!cost_centre_id) return null;

  const code = String(c?.code ?? "").trim();
  const name =
    String(c?.name ?? c?.cost_centre_name ?? "").trim() ||
    `Cost Centre ${cost_centre_id}`;

  return {
    cost_centre_id,
    code,
    name,
    label: code ? `${code} - ${name}` : name,
    raw: c,
  };
}

function normalizeMenuCatalogRow(m) {
  const item_id = normalizeMenuId(m);
  if (!item_id) return null;

  const name = String(m?.name ?? m?.item_name ?? "").trim() || `Item ${item_id}`;
  const code = String(m?.code ?? m?.item_code ?? "").trim() || "";
  const category = String(m?.category ?? "").trim();
  const type = normalizeCategoryToType(category);

  return {
    item_id,
    menu_item_id: item_id,
    name,
    code,
    category,
    type,
    unit_cost: num(m?.unit_cost ?? m?.cost ?? m?.effective_cost ?? 0, 0),
    unit_price: num(m?.unit_price ?? m?.price ?? m?.effective_sale_price ?? 0, 0),
    raw: m,
  };
}

function buildClientDisplayText(client) {
  if (!client || typeof client !== "object") return "";

  const clientName = String(client.client_name ?? client.name ?? "").trim();
  const email = String(client.email ?? "").trim();
  const invoicingEmail = String(client.invoicing_email ?? "").trim();
  const phone = String(client.phone ?? "").trim();
  const companyName = String(client.company_name ?? client.company ?? "").trim();

  const primary = clientName || "Unnamed client";
  const secondary = [companyName, email || invoicingEmail, phone].filter(Boolean).join(" · ");

  return secondary ? `${primary} — ${secondary}` : primary;
}

function normalizeClientRow(c) {
  const client_id = toPositiveIntOrNull(c?.client_id ?? c?.id);
  if (!client_id) return null;

  const client_name =
    String(c?.client_name ?? c?.name ?? "").trim() || `Client ${client_id}`;
  const email = String(c?.email ?? "").trim();
  const phone = String(c?.phone ?? "").trim();
  const company_name = String(c?.company_name ?? c?.company ?? "").trim();
  const notes = String(c?.notes ?? "").trim();

  const invoicing_email = String(c?.invoicing_email ?? "").trim();
  const billing_contact_name = String(c?.billing_contact_name ?? "").trim();
  const billing_phone = String(c?.billing_phone ?? "").trim();

  const address_line_1 = String(c?.address_line_1 ?? "").trim();
  const address_line_2 = String(c?.address_line_2 ?? "").trim();
  const city = String(c?.city ?? "").trim();
  const county = String(c?.county ?? "").trim();
  const postcode = String(c?.postcode ?? "").trim();
  const country = String(c?.country ?? "").trim();

  return {
    client_id,
    client_name,
    email,
    phone,
    company_name,
    notes,
    invoicing_email,
    billing_contact_name,
    billing_phone,
    address_line_1,
    address_line_2,
    city,
    county,
    postcode,
    country,
    label: buildClientDisplayText({
      client_name,
      email,
      phone,
      company_name,
      invoicing_email,
    }),
    raw: c,
  };
}

async function tryGet(urls) {
  let lastErr = null;

  for (const url of urls) {
    try {
      return await api.get(url);
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr || new Error("GET request failed");
}

function SelectedLineTable({
  title,
  accent = "slate",
  lines,
  saving,
  onDecrease,
  onIncrease,
  onUpdate,
  onRemove,
}) {
  const sectionPortions = lines.reduce((a, l) => a + num(l.qty), 0);
  const sectionSubtotal = lines.reduce((a, l) => a + num(l.qty) * num(l.unit_price), 0);

  const accentHeader =
    accent === "emerald"
      ? "border-emerald-200 bg-emerald-50/70"
      : "border-sky-200 bg-sky-50/70";

  const accentBadge =
    accent === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : "border-sky-200 bg-sky-50 text-sky-800";

  if (!lines.length) {
    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className={`flex items-center justify-between border-b px-4 py-3 ${accentHeader}`}>
          <div className="font-semibold text-slate-900">{title}</div>
          <Badge className={accentBadge}>0 lines</Badge>
        </div>
        <div className="px-4 py-5 text-sm text-slate-600">No {title.toLowerCase()} items selected.</div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div
        className={`flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:items-center md:justify-between ${accentHeader}`}
      >
        <div>
          <div className="font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-600">
            {lines.length} lines · {sectionPortions} portions
          </div>
        </div>

        <div className="text-sm text-slate-700">
          Subtotal <span className="font-semibold text-slate-900">{currency(sectionSubtotal)}</span>
        </div>
      </div>

      <div className="hidden border-b border-slate-200 bg-slate-50/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600 xl:grid xl:grid-cols-[minmax(0,1.9fr)_190px_140px_120px_96px] xl:gap-3">
        <div>Item</div>
        <div>Quantity</div>
        <div className="text-right">Unit price</div>
        <div className="text-right">Line total</div>
        <div className="text-right">Action</div>
      </div>

      <div className="divide-y divide-slate-200">
        {lines.map((line) => {
          const lineTotal = num(line.unit_price) * num(line.qty);

          return (
            <div
              key={line.item_id}
              className="px-4 py-4 xl:grid xl:grid-cols-[minmax(0,1.9fr)_190px_140px_120px_96px] xl:items-center xl:gap-3"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="truncate text-sm font-semibold text-slate-900">{line.name}</div>
                  <Badge variant="outline" className="rounded-full text-[11px]">
                    {line.type}
                  </Badge>
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {line.code ? `${line.code} · ` : ""}
                  {line.category || "No category"}
                </div>
              </div>

              <div className="mt-4 xl:mt-0">
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-600 xl:hidden">
                  Quantity
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="h-9 w-9 rounded-xl"
                    onClick={() => onDecrease(line.item_id)}
                    disabled={saving}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>

                  <Input
                    value={line.qty}
                    onChange={(e) =>
                      onUpdate(line.item_id, {
                        qty: clamp(Number(String(e.target.value).replace(/[^0-9]/g, ""))),
                      })
                    }
                    inputMode="numeric"
                    className={`${calmField} h-9 rounded-xl text-center`}
                    disabled={saving}
                  />

                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="h-9 w-9 rounded-xl"
                    onClick={() => onIncrease(line.item_id)}
                    disabled={saving}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="mt-4 xl:mt-0">
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-600 xl:hidden">
                  Unit price
                </div>
                <Input
                  value={line.unit_price}
                  onChange={(e) =>
                    onUpdate(line.item_id, {
                      unit_price: toMoneyInputString(e.target.value),
                    })
                  }
                  inputMode="decimal"
                  className={`${calmField} h-9 rounded-xl text-right`}
                  disabled={saving}
                />
              </div>

              <div className="mt-4 xl:mt-0">
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-600 xl:hidden">
                  Line total
                </div>
                <div className="flex h-9 items-center justify-end rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-900">
                  {currency(lineTotal)}
                </div>
              </div>

              <div className="mt-4 flex xl:mt-0 xl:justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="rounded-2xl hover:bg-red-50"
                  onClick={() => onRemove(line.item_id)}
                  disabled={saving}
                  title="Remove"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  <span className="xl:hidden">Remove</span>
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MenuPickerModal({
  open,
  onClose,
  menuItems,
  selectedMap,
  saving,
  menuQ,
  setMenuQ,
  typeFilter,
  setTypeFilter,
  addLine,
}) {
  const filteredMenu = useMemo(() => {
    const q = menuQ.trim().toLowerCase();

    return menuItems.filter((m) => {
      const type = normalizeCategoryToType(m.category);
      if (typeFilter !== "all" && type !== typeFilter) return false;

      if (!q) return true;

      return [m.name, m.category, m.code].some((f) =>
        String(f || "").toLowerCase().includes(q)
      );
    });
  }, [menuItems, menuQ, typeFilter]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4">
      <div className="flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <div className="flex items-center gap-2 text-slate-950">
              <UtensilsCrossed className="h-5 w-5 text-sky-700" />
              <h3 className="text-lg font-semibold">Menu Items</h3>
            </div>
            <div className="mt-1 text-sm text-slate-600">
              Search and add dishes or drinks to the event.
            </div>
          </div>

          <Button
            type="button"
            variant="ghost"
            className="rounded-2xl"
            onClick={onClose}
            disabled={saving}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <Label className={strongLabel}>Search menu items</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input
                  value={menuQ}
                  onChange={(e) => setMenuQ(e.target.value)}
                  placeholder="Search by dish or drink name"
                  className={`${calmField} pl-10 pr-10`}
                  disabled={saving}
                />
                {menuQ ? (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800"
                    onClick={() => setMenuQ("")}
                    title="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {[
                { k: "all", label: "All" },
                { k: "food", label: "Food" },
                { k: "drink", label: "Drinks" },
              ].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setTypeFilter(t.k)}
                  disabled={saving}
                  className={
                    "rounded-full border px-3 py-2 text-xs font-medium transition " +
                    (typeFilter === t.k
                      ? "border-sky-200 bg-sky-50 text-sky-800"
                      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3 text-xs text-slate-700">
            Showing <span className="font-semibold text-slate-900">{filteredMenu.length}</span> of{" "}
            <span className="font-semibold text-slate-900">{menuItems.length}</span> items
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {filteredMenu.length ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {filteredMenu.map((m) => {
                const selected = selectedMap.get(m.item_id);

                return (
                  <div
                    key={m.item_id}
                    className={
                      "rounded-2xl border p-4 transition " +
                      (selected
                        ? "border-emerald-200 bg-emerald-50/70"
                        : "border-slate-200 bg-slate-50/60 hover:bg-slate-50")
                    }
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-950">
                          {m.name || "Unnamed item"}
                        </div>
                        <div className="mt-1 text-xs text-slate-600">
                          {m.type === "drink" ? "Drink" : "Dish"}
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        <div className="text-[11px] uppercase tracking-wide text-slate-600">
                          Price
                        </div>
                        <div className="font-semibold text-slate-950">{currency(m.unit_price)}</div>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3">
                      <div className="text-xs text-slate-600">
                        {selected ? (
                          <span className="font-medium text-emerald-700">
                            Selected · Qty {selected.qty}
                          </span>
                        ) : (
                          <span>Not selected</span>
                        )}
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        onClick={() => addLine(m)}
                        className="rounded-2xl"
                        disabled={saving}
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        {selected ? "Add one more" : "Add"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-slate-700">
              No menu items match your search.
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-4">
          <div className="text-sm text-slate-700">
            Selected{" "}
            <span className="font-semibold text-slate-950">{selectedMap.size}</span> unique menu items
          </div>

          <Button
            type="button"
            variant="secondary"
            className="rounded-2xl"
            onClick={onClose}
            disabled={saving}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function EventFormPage() {
  const params = useParams();
  const navigate = useNavigate();

  const resolvedEventId = params.id ?? params.eventId ?? null;
  const isEdit = Boolean(resolvedEventId);

  const [formData, setFormData] = useState({
    event_name: "",
    event_date: "",
    kickoff_time: "",
    end_time: "",
    expected_guests: "",
    venue_name: "",
    event_theme: "",
    planner_id: "",
    client_id: "",
    notes: "",
    room_hire: 0,
    tax_rate: 5,
    service_rate: 10,
    discount: 0,
  });

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState("build");
  const [menuPickerOpen, setMenuPickerOpen] = useState(false);

  const [planners, setPlanners] = useState([]);
  const [clients, setClients] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [selectedStaffingCostCentre, setSelectedStaffingCostCentre] = useState("");

  const [clientMode, setClientMode] = useState("existing");
  const [clientSearch, setClientSearch] = useState("");
  const [newClient, setNewClient] = useState({
    client_name: "",
    email: "",
    phone: "",
    company_name: "",
    invoicing_email: "",
    billing_contact_name: "",
    billing_phone: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    county: "",
    postcode: "",
    country: "",
    notes: "",
  });

  const [menuItems, setMenuItems] = useState([]);
  const [menuQ, setMenuQ] = useState("");
  const [lines, setLines] = useState([]);
  const [staffingLines, setStaffingLines] = useState([]);

  const [typeFilter, setTypeFilter] = useState("all");

  const didPrefillLinesRef = useRef(false);
  const didPrefillStaffingRef = useRef(false);

  const totals = useMemo(() => {
    const food = lines.filter((l) => l.type === "food");
    const drinks = lines.filter((l) => l.type === "drink");
    const sum = (arr, f) => arr.reduce((a, b) => a + num(b[f]) * num(b.qty), 0);

    const foodSales = sum(food, "unit_price");
    const drinkSales = sum(drinks, "unit_price");
    const foodCost = sum(food, "unit_cost");
    const drinkCost = sum(drinks, "unit_cost");
    const staffingForecast = staffingLines.reduce((acc, row) => acc + num(row.amount, 0), 0);

    const salesSub = foodSales + drinkSales + num(formData.room_hire);
    const tax = (num(formData.tax_rate) / 100) * salesSub;
    const service = (num(formData.service_rate) / 100) * salesSub;
    const gross = salesSub + tax + service - num(formData.discount);
    const cogs = foodCost + drinkCost;
    const gp = gross - cogs;
    const netAfterStaffing = gp - staffingForecast;
    const margin = salesSub > 0 ? (gp / salesSub) * 100 : 0;
    const netMarginAfterStaffing = salesSub > 0 ? (netAfterStaffing / salesSub) * 100 : 0;

    return {
      foodSales,
      drinkSales,
      foodCost,
      drinkCost,
      staffingForecast,
      salesSub,
      tax,
      service,
      gross,
      cogs,
      gp,
      netAfterStaffing,
      margin,
      netMarginAfterStaffing,
    };
  }, [
    lines,
    staffingLines,
    formData.room_hire,
    formData.tax_rate,
    formData.service_rate,
    formData.discount,
  ]);

  const selectedSubtotal = useMemo(
    () => lines.reduce((acc, l) => acc + num(l.unit_price) * num(l.qty), 0),
    [lines]
  );

  const selectedCount = useMemo(
    () => lines.reduce((a, l) => a + num(l.qty), 0),
    [lines]
  );

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients;

    return clients.filter((c) =>
      [
        c.client_name,
        c.email,
        c.invoicing_email,
        c.phone,
        c.billing_phone,
        c.company_name,
        c.billing_contact_name,
        c.postcode,
        c.city,
      ].some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [clients, clientSearch]);

  const selectedClient = useMemo(() => {
    const selectedId = toPositiveIntOrNull(formData.client_id);
    if (!selectedId) return null;

    return clients.find((c) => Number(c.client_id) === Number(selectedId)) || null;
  }, [clients, formData.client_id]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [pl, mi, cc] = await Promise.all([
          tryGet(["/event/planners", "/planners"]),
          tryGet(["/inventory/menu-items"]),
          tryGet(["/cost-centres", "/cost-centres/active"]),
        ]);

        let clientResponse = null;
        try {
          clientResponse = await tryGet(["/event/clients", "/events/clients"]);
        } catch (clientErr) {
          console.error("Failed to load clients:", clientErr);
          clientResponse = { data: [] };
        }

        if (cancelled) return;

        const plannerRows = asRows(pl.data).map(normalizePlannerRow).filter(Boolean);
        const uniquePlanners = Array.from(
          new Map(plannerRows.map((p) => [p.planner_id, p])).values()
        );

        const menuRows = asRows(mi.data).map(normalizeMenuCatalogRow).filter(Boolean);

        const rawCostCentreRows = [...asRows(cc.data), ...extractCostCentreRows(cc.data)];
        const costCentreRows = rawCostCentreRows.map(normalizeCostCentreRow).filter(Boolean);
        const uniqueCostCentres = Array.from(
          new Map(costCentreRows.map((c) => [c.cost_centre_id, c])).values()
        );

        const clientRows = asRows(clientResponse?.data).map(normalizeClientRow).filter(Boolean);
        const uniqueClients = Array.from(
          new Map(clientRows.map((c) => [c.client_id, c])).values()
        );

        setPlanners(uniquePlanners);
        setMenuItems(menuRows);
        setCostCentres(uniqueCostCentres);
        setClients(uniqueClients);
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        toast.error("Failed to load planners, menu, or cost centres");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit || !resolvedEventId) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const { data } = await tryGet([
          `/event/${resolvedEventId}`,
          `/event/events/${resolvedEventId}`,
          `/events/${resolvedEventId}`,
        ]);

        if (cancelled) return;

        const e = normalizeEventResponse(data);
        const plannerId = toPositiveIntOrNull(
          e.planner_id ??
            e.plannerId ??
            e.planner?.planner_id ??
            e.planner?.id ??
            e.event_planner_id
        );
        const clientId = toPositiveIntOrNull(
          e.client_id ?? e.clientId ?? e.client?.client_id ?? e.client?.id
        );

        setFormData((prev) => ({
          ...prev,
          event_name: e.event_name ?? e.name ?? e.eventName ?? "",
          event_date: isoDateOnly(
            e.event_date ?? e.date ?? e.eventDate ?? e.starts_at ?? e.startsAt ?? ""
          ),
          kickoff_time: timeHHMM(
            e.kickoff_time ??
              e.start_time ??
              e.startTime ??
              e.kickoff ??
              e.starts_at ??
              e.startsAt ??
              ""
          ),
          end_time: timeHHMM(
            e.end_time ?? e.endTime ?? e.finish_time ?? e.ends_at ?? e.endsAt ?? ""
          ),
          expected_guests:
            e.expected_guests ?? e.expectedGuests ?? e.guests ?? e.guest_count ?? "",
          venue_name: e.venue_name ?? e.venueName ?? e.venue ?? e.location ?? "",
          event_theme: e.event_theme ?? e.eventTheme ?? e.theme ?? "",
          planner_id: plannerId || "",
          client_id: clientId || "",
          notes: e.notes ?? e.description ?? "",
          room_hire: e.room_hire ?? e.roomHire ?? 0,
          tax_rate: e.tax_rate ?? e.taxRate ?? 5,
          service_rate: e.service_rate ?? e.serviceRate ?? 10,
          discount: e.discount ?? 0,
        }));

        if (e.client && typeof e.client === "object") {
          const normalizedLoadedClient = normalizeClientRow(e.client);

          setNewClient((prev) => ({
            ...prev,
            client_name: String(e.client.client_name ?? e.client.name ?? "").trim(),
            email: String(e.client.email ?? "").trim(),
            phone: String(e.client.phone ?? "").trim(),
            company_name: String(e.client.company_name ?? e.client.company ?? "").trim(),
            invoicing_email: String(e.client.invoicing_email ?? "").trim(),
            billing_contact_name: String(e.client.billing_contact_name ?? "").trim(),
            billing_phone: String(e.client.billing_phone ?? "").trim(),
            address_line_1: String(e.client.address_line_1 ?? "").trim(),
            address_line_2: String(e.client.address_line_2 ?? "").trim(),
            city: String(e.client.city ?? "").trim(),
            county: String(e.client.county ?? "").trim(),
            postcode: String(e.client.postcode ?? "").trim(),
            country: String(e.client.country ?? "").trim(),
            notes: String(e.client.notes ?? "").trim(),
          }));

          if (normalizedLoadedClient) {
            setClients((prev) => {
              const merged = [...prev, normalizedLoadedClient];
              return Array.from(new Map(merged.map((c) => [c.client_id, c])).values());
            });
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        toast.error("Failed to load event data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, resolvedEventId]);

  useEffect(() => {
    if (!isEdit || !resolvedEventId) return;
    if (didPrefillLinesRef.current) return;
    if (!menuItems.length) return;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await tryGet([
          `/event/${resolvedEventId}/items`,
          `/event/events/${resolvedEventId}/items`,
          `/events/${resolvedEventId}/items`,
        ]);

        if (cancelled) return;

        const items = asRows(data);
        const catalogById = new Map(menuItems.map((m) => [Number(m.item_id), m]));

        const nextLines = items
          .map((it) => {
            const menu_item_id = toPositiveIntOrNull(it.menu_item_id ?? it.item_id ?? it.id);
            if (!menu_item_id) return null;

            const cat = catalogById.get(menu_item_id);
            const name =
              String(it.name ?? it.item_name ?? cat?.name ?? "").trim() || `Item ${menu_item_id}`;
            const code = String(it.code ?? it.item_code ?? cat?.code ?? "").trim() || "";
            const type = normalizeCategoryToType(it.category ?? cat?.category);
            const qty = num(it.qty ?? it.quantity ?? it.servings ?? it.expected_servings ?? 0, 0);
            const unit_price = num(
              it.event_sale_price ?? it.unit_price ?? it.sell_price ?? cat?.unit_price ?? 0,
              0
            );
            const unit_cost = num(cat?.unit_cost ?? 0, 0);

            if (qty <= 0) return null;

            return {
              item_id: menu_item_id,
              code,
              name,
              type,
              category: cat?.category || it.category || "",
              qty,
              unit_cost,
              unit_price,
            };
          })
          .filter(Boolean);

        setLines(nextLines);
        didPrefillLinesRef.current = true;
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        didPrefillLinesRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, resolvedEventId, menuItems]);

  useEffect(() => {
    if (!isEdit || !resolvedEventId) return;
    if (didPrefillStaffingRef.current) return;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await tryGet([
          `/event/${resolvedEventId}/staffing-costs`,
          `/event/${resolvedEventId}/staffing/costs`,
          `/events/${resolvedEventId}/staffing-costs`,
          `/events/${resolvedEventId}/staffing/costs`,
        ]);

        if (cancelled) return;

        const fetchedCostCentres = extractCostCentreRows(data)
          .map(normalizeCostCentreRow)
          .filter(Boolean);

        if (fetchedCostCentres.length) {
          setCostCentres((prev) => {
            const merged = [...prev, ...fetchedCostCentres];
            return Array.from(new Map(merged.map((c) => [c.cost_centre_id, c])).values());
          });
        }

        const rows = extractForecastStaffingRows(data)
          .map((row) => {
            const cost_centre_id = toPositiveIntOrNull(row?.cost_centre_id);
            if (!cost_centre_id) return null;

            return {
              cost_centre_id,
              amount: num(row?.amount, 0),
              notes: String(row?.notes ?? "").trim(),
            };
          })
          .filter(Boolean);

        setStaffingLines(rows);
        didPrefillStaffingRef.current = true;
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        didPrefillStaffingRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, resolvedEventId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewClientChange = (e) => {
    const { name, value } = e.target;
    setNewClient((prev) => ({ ...prev, [name]: value }));
  };

  const updateLine = (id, patch) =>
    setLines((prev) => prev.map((l) => (l.item_id === id ? { ...l, ...patch } : l)));

  const removeLine = (id) => setLines((prev) => prev.filter((l) => l.item_id !== id));

  const clearAllLines = () => {
    if (!lines.length) return;
    const ok = window.confirm("Clear all selected items?");
    if (!ok) return;
    setLines([]);
  };

  const addLine = useCallback((m) => {
    const itemId = toPositiveIntOrNull(m?.menu_item_id ?? m?.item_id);
    if (!itemId) return;

    setLines((prev) => {
      const idx = prev.findIndex((l) => l.item_id === itemId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: clamp(num(next[idx].qty) + 1, 0) };
        return next;
      }

      return [
        ...prev,
        {
          item_id: itemId,
          code: m.code || "",
          name: m.name || `Item ${itemId}`,
          type: normalizeCategoryToType(m.category),
          category: m.category || "",
          qty: 1,
          unit_cost: num(m.unit_cost ?? 0),
          unit_price: num(m.unit_price ?? 0),
        },
      ];
    });
  }, []);

  const decQty = (id) => {
    setLines((prev) =>
      prev
        .map((l) => (l.item_id === id ? { ...l, qty: clamp(num(l.qty) - 1, 0) } : l))
        .filter((l) => num(l.qty) > 0)
    );
  };

  const incQty = (id) => {
    setLines((prev) =>
      prev.map((l) => (l.item_id === id ? { ...l, qty: clamp(num(l.qty) + 1, 0) } : l))
    );
  };

  const selectedMap = useMemo(() => {
    const m = new Map();
    for (const l of lines) m.set(l.item_id, l);
    return m;
  }, [lines]);

  const selectedStaffingCentreIds = useMemo(
    () => new Set(staffingLines.map((r) => Number(r.cost_centre_id)).filter(Boolean)),
    [staffingLines]
  );

  const availableCostCentres = useMemo(() => {
    return costCentres.filter((c) => !selectedStaffingCentreIds.has(Number(c.cost_centre_id)));
  }, [costCentres, selectedStaffingCentreIds]);

  const linesFood = useMemo(() => lines.filter((l) => l.type === "food"), [lines]);
  const linesDrink = useMemo(() => lines.filter((l) => l.type === "drink"), [lines]);

  const addStaffingLine = (costCentreIdRaw) => {
    const cost_centre_id = toPositiveIntOrNull(costCentreIdRaw);
    if (!cost_centre_id) return;

    setStaffingLines((prev) => {
      if (prev.some((row) => Number(row.cost_centre_id) === Number(cost_centre_id))) {
        return prev;
      }

      return [
        ...prev,
        {
          cost_centre_id,
          amount: 0,
          notes: "",
        },
      ];
    });

    setSelectedStaffingCostCentre("");
  };

  const updateStaffingLine = (costCentreId, patch) => {
    setStaffingLines((prev) =>
      prev.map((row) =>
        Number(row.cost_centre_id) === Number(costCentreId) ? { ...row, ...patch } : row
      )
    );
  };

  const removeStaffingLine = (costCentreId) => {
    setStaffingLines((prev) =>
      prev.filter((row) => Number(row.cost_centre_id) !== Number(costCentreId))
    );
  };

  const clearAllStaffingLines = () => {
    if (!staffingLines.length) return;
    const ok = window.confirm("Clear all staffing forecast lines?");
    if (!ok) return;
    setStaffingLines([]);
    setSelectedStaffingCostCentre("");
  };

  const bulkSetQtyToGuests = () => {
    const guests = clamp(formData.expected_guests, 0, 1_000_000);
    if (!guests || !lines.length) return;
    const ok = window.confirm(`Set ALL selected item quantities to ${guests}?`);
    if (!ok) return;
    setLines((prev) => prev.map((l) => ({ ...l, qty: guests })));
  };

  const bulkResetPricesFromCatalog = () => {
    if (!lines.length) return;
    const ok = window.confirm("Reset sale prices from the menu catalog for all selected lines?");
    if (!ok) return;

    setLines((prev) =>
      prev.map((l) => {
        const cat = menuItems.find((m) => Number(m.item_id) === Number(l.item_id));
        if (!cat) return l;
        return {
          ...l,
          unit_cost: num(cat.unit_cost ?? l.unit_cost ?? 0),
          unit_price: num(cat.unit_price ?? l.unit_price ?? 0),
        };
      })
    );
  };

  const hasInlineClientValues = useMemo(() => {
    return Boolean(
      String(newClient.client_name || "").trim() ||
        String(newClient.email || "").trim() ||
        String(newClient.phone || "").trim() ||
        String(newClient.company_name || "").trim() ||
        String(newClient.invoicing_email || "").trim() ||
        String(newClient.billing_contact_name || "").trim() ||
        String(newClient.billing_phone || "").trim() ||
        String(newClient.address_line_1 || "").trim() ||
        String(newClient.address_line_2 || "").trim() ||
        String(newClient.city || "").trim() ||
        String(newClient.county || "").trim() ||
        String(newClient.postcode || "").trim() ||
        String(newClient.country || "").trim() ||
        String(newClient.notes || "").trim()
    );
  }, [newClient]);

  const validateBuildStep = () => {
    const plannerId = toPositiveIntOrNull(formData.planner_id);

    if (!formData.event_name || !formData.event_date || !plannerId) {
      toast.error("Please fill Event Name, Date and Planner");
      return false;
    }

    if (formData.end_time && formData.kickoff_time && formData.end_time <= formData.kickoff_time) {
      toast.error("End time must be after start time");
      return false;
    }

    if (clientMode === "existing") {
      if (formData.client_id && !toPositiveIntOrNull(formData.client_id)) {
        toast.error("Selected client is invalid");
        return false;
      }
    }

    if (clientMode === "new") {
      const clientName = String(newClient.client_name || "").trim();

      if (hasInlineClientValues && !clientName) {
        toast.error("New client name is required");
        return false;
      }

      if (!isValidEmail(newClient.email)) {
        toast.error("Client email looks invalid");
        return false;
      }

      if (!isValidEmail(newClient.invoicing_email)) {
        toast.error("Invoicing email looks invalid");
        return false;
      }
    }

    return true;
  };

  const handleNextStep = () => {
    if (!validateBuildStep()) return;
    setCurrentStep("commercial");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrevStep = () => {
    setCurrentStep("build");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;

    const plannerId = toPositiveIntOrNull(formData.planner_id);

    if (!formData.event_name || !formData.event_date || !plannerId) {
      toast.error("Please fill Event Name, Date and Planner");
      return;
    }

    if (formData.end_time && formData.kickoff_time && formData.end_time <= formData.kickoff_time) {
      toast.error("End time must be after start time");
      return;
    }

    const selectedClientId =
      clientMode === "existing" ? toPositiveIntOrNull(formData.client_id) : null;

    const clientDraft =
      clientMode === "new" && hasInlineClientValues
        ? {
            client_name: String(newClient.client_name || "").trim(),
            email: String(newClient.email || "").trim() || null,
            phone: String(newClient.phone || "").trim() || null,
            company_name: String(newClient.company_name || "").trim() || null,
            invoicing_email: String(newClient.invoicing_email || "").trim() || null,
            billing_contact_name: String(newClient.billing_contact_name || "").trim() || null,
            billing_phone: String(newClient.billing_phone || "").trim() || null,
            address_line_1: String(newClient.address_line_1 || "").trim() || null,
            address_line_2: String(newClient.address_line_2 || "").trim() || null,
            city: String(newClient.city || "").trim() || null,
            county: String(newClient.county || "").trim() || null,
            postcode: String(newClient.postcode || "").trim() || null,
            country: String(newClient.country || "").trim() || null,
            notes: String(newClient.notes || "").trim() || null,
          }
        : null;

    if (clientMode === "new" && clientDraft && !clientDraft.client_name) {
      toast.error("New client name is required");
      return;
    }

    if (clientDraft?.email && !isValidEmail(clientDraft.email)) {
      toast.error("Client email looks invalid");
      return;
    }

    if (clientDraft?.invoicing_email && !isValidEmail(clientDraft.invoicing_email)) {
      toast.error("Invoicing email looks invalid");
      return;
    }

    setSaving(true);

    try {
      const planned_items = buildPlannedItems(lines);
      const staffing_cost_lines = buildStaffingCostLines(staffingLines);

      const payload = {
        ...formData,
        planner_id: plannerId,
        client_id: selectedClientId || null,
        client: clientDraft,
        event_client: clientDraft,
        expected_guests:
          formData.expected_guests === ""
            ? ""
            : clamp(formData.expected_guests, 0, 1_000_000),
        room_hire: toMoneyInputString(formData.room_hire),
        tax_rate: toMoneyInputString(formData.tax_rate),
        service_rate: toMoneyInputString(formData.service_rate),
        discount: toMoneyInputString(formData.discount),
        planned_items,
        staffing_cost_lines,
        ...(isEdit
          ? {
              replace_planned_items: true,
              replace_staffing_cost_lines: true,
            }
          : {}),
      };

      if (isEdit && resolvedEventId) {
        await api.put(`/event/events/${resolvedEventId}`, payload);
        toast.success("Event updated");
        navigate("/events", {
          state: { toast: "Event updated", openId: resolvedEventId },
        });
        return;
      }

      const { data } = await api.post("/event/events", payload);
      const newId =
        data?.event_id ??
        data?.id ??
        data?.event?.event_id ??
        data?.event?.id ??
        null;

      if (!newId) throw new Error("Event created but id missing");

      toast.success("Event created");
      navigate("/events", { state: { toast: "Event created", openId: newId } });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error saving event"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[100dvh] w-full bg-gradient-to-br from-slate-50 via-cyan-50 to-white text-slate-900">
      <MenuPickerModal
        open={menuPickerOpen}
        onClose={() => setMenuPickerOpen(false)}
        menuItems={menuItems}
        selectedMap={selectedMap}
        saving={saving}
        menuQ={menuQ}
        setMenuQ={setMenuQ}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        addLine={addLine}
      />

      <form onSubmit={handleSubmit} className="mx-auto max-w-[1500px] px-5 py-6">
        <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-start gap-3">
            <Button
              type="button"
              variant="secondary"
              className="gap-2 rounded-2xl"
              onClick={() => navigate(-1)}
              disabled={saving}
              title="Back"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>

            <div>
              <h2 className="text-2xl font-bold text-slate-950 md:text-3xl">
                {isEdit ? "Edit Event" : "Create Event"}
              </h2>
              <div className="mt-1 text-sm text-slate-700">
                Build the event, menu, pricing, staffing forecast, and client context in one place.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-sm">
              <span
                className={`inline-flex h-2.5 w-2.5 rounded-full ${
                  currentStep === "build" ? "bg-sky-500" : "bg-emerald-500"
                }`}
              />
              {currentStep === "build" ? "Step 1 · Build Event" : "Step 2 · Quotation & Profit"}
            </div>

            {lines.length ? (
              <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-800">
                <Sparkles className="mr-1 h-3.5 w-3.5" />
                {lines.length} lines · {selectedCount} portions
              </Badge>
            ) : (
              <Badge variant="outline">No menu items selected</Badge>
            )}
          </div>
        </div>

        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white px-5 py-8 text-slate-600 shadow-sm">
            Loading event data…
          </div>
        ) : (
          <div className="space-y-6">
            {currentStep === "build" ? (
              <>
                <Card className="rounded-3xl border-slate-200 bg-white shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-slate-950">
                      <Calendar className="h-5 w-5 text-sky-700" /> Event Details
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <Label className={strongLabel}>Event Name</Label>
                      <Input
                        name="event_name"
                        value={formData.event_name}
                        onChange={handleChange}
                        placeholder="e.g. Acme Gala"
                        required
                        className={calmField}
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <Label className={strongLabel}>Event Date</Label>
                      <Input
                        type="date"
                        name="event_date"
                        value={formData.event_date}
                        onChange={handleChange}
                        required
                        className={calmField}
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <Label className={`flex items-center gap-2 ${strongLabel}`}>
                        <Clock className="h-4 w-4 text-sky-700" /> Start
                      </Label>
                      <Input
                        type="time"
                        name="kickoff_time"
                        value={formData.kickoff_time}
                        onChange={handleChange}
                        className={calmField}
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <Label className={`flex items-center gap-2 ${strongLabel}`}>
                        <Clock className="h-4 w-4 text-sky-700" /> End
                      </Label>
                      <Input
                        type="time"
                        name="end_time"
                        value={formData.end_time}
                        onChange={handleChange}
                        className={calmField}
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <Label className={strongLabel}>Expected Guests</Label>
                      <Input
                        type="number"
                        name="expected_guests"
                        value={formData.expected_guests}
                        onChange={handleChange}
                        className={calmField}
                        disabled={saving}
                        min={0}
                      />
                    </div>

                    <div>
                      <Label className={`flex items-center gap-2 ${strongLabel}`}>
                        <Building2 className="h-4 w-4 text-sky-700" /> Venue
                      </Label>
                      <Input
                        name="venue_name"
                        value={formData.venue_name}
                        onChange={handleChange}
                        placeholder="Venue / Room"
                        className={calmField}
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <Label className={strongLabel}>Theme</Label>
                      <Input
                        name="event_theme"
                        value={formData.event_theme}
                        onChange={handleChange}
                        className={calmField}
                        disabled={saving}
                      />
                    </div>

                    <div>
                      <Label className={`flex items-center gap-2 ${strongLabel}`}>
                        <User2 className="h-4 w-4 text-sky-700" /> Planner
                      </Label>
                      <Select
                        value={formData.planner_id === "" ? "" : String(formData.planner_id)}
                        onValueChange={(v) => {
                          setFormData((p) => ({
                            ...p,
                            planner_id: toPositiveIntOrNull(v) || "",
                          }));
                        }}
                        disabled={saving}
                      >
                        <SelectTrigger className={calmSelectTrigger}>
                          <SelectValue placeholder="Select planner" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {planners.map((p) => (
                            <SelectItem key={p.planner_id} value={String(p.planner_id)}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="md:col-span-2">
                      <Label className={strongLabel}>Notes</Label>
                      <Textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        placeholder="Special requests, dietary notes, AV…"
                        className={calmArea}
                        disabled={saving}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-3xl border-slate-200 bg-white shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-slate-950">
                      <Users className="h-5 w-5 text-sky-700" /> Client
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-5">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setClientMode("existing")}
                        disabled={saving}
                        className={
                          "rounded-full border px-3 py-2 text-xs font-medium transition " +
                          (clientMode === "existing"
                            ? "border-sky-200 bg-sky-50 text-sky-800"
                            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
                        }
                      >
                        Select existing client
                      </button>

                      <button
                        type="button"
                        onClick={() => setClientMode("new")}
                        disabled={saving}
                        className={
                          "rounded-full border px-3 py-2 text-xs font-medium transition " +
                          (clientMode === "new"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
                        }
                      >
                        Enter new client
                      </button>
                    </div>

                    {clientMode === "existing" ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                          <div>
                            <Label className={strongLabel}>Search clients</Label>
                            <Input
                              value={clientSearch}
                              onChange={(e) => setClientSearch(e.target.value)}
                              placeholder="Search by name, email, billing email, phone, company, address"
                              className={calmField}
                              disabled={saving}
                            />
                          </div>

                          <div>
                            <Label className={strongLabel}>Client</Label>
                            <Select
                              value={formData.client_id === "" ? "" : String(formData.client_id)}
                              onValueChange={(v) => {
                                setFormData((p) => ({
                                  ...p,
                                  client_id: toPositiveIntOrNull(v) || "",
                                }));
                              }}
                              disabled={saving}
                            >
                              <SelectTrigger className={calmSelectTrigger}>
                                <SelectValue placeholder="Optional: select a client" />
                              </SelectTrigger>
                              <SelectContent className="max-h-80">
                                {filteredClients.length ? (
                                  filteredClients.map((c) => (
                                    <SelectItem key={c.client_id} value={String(c.client_id)}>
                                      {buildClientDisplayText(c)}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="__empty_clients_state" disabled>
                                    No matching clients
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {selectedClient ? (
                          <div className="rounded-2xl border border-sky-200 bg-sky-50/70 p-4">
                            <div className="font-semibold text-slate-950">
                              {selectedClient.client_name || "Unnamed client"}
                            </div>

                            <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-slate-700 md:grid-cols-2">
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-sky-700" />
                                <span>{selectedClient.email || "No primary email"}</span>
                              </div>

                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-sky-700" />
                                <span>{selectedClient.phone || "No primary phone"}</span>
                              </div>

                              <div className="md:col-span-2">
                                <span className="font-medium">Company:</span>{" "}
                                {selectedClient.company_name || "—"}
                              </div>

                              <div>
                                <span className="font-medium">Invoicing email:</span>{" "}
                                {selectedClient.invoicing_email || "—"}
                              </div>

                              <div>
                                <span className="font-medium">Billing contact:</span>{" "}
                                {selectedClient.billing_contact_name || "—"}
                              </div>

                              <div>
                                <span className="font-medium">Billing phone:</span>{" "}
                                {selectedClient.billing_phone || "—"}
                              </div>

                              <div className="md:col-span-2">
                                <span className="font-medium">Address:</span>{" "}
                                {formatClientAddress(selectedClient) || "—"}
                              </div>

                              {selectedClient.notes ? (
                                <div className="md:col-span-2">
                                  <span className="font-medium">Notes:</span>{" "}
                                  {selectedClient.notes}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                            No client selected yet. You can leave this empty for now, or switch to new client entry.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div>
                            <Label className={strongLabel}>Client Name</Label>
                            <Input
                              name="client_name"
                              value={newClient.client_name}
                              onChange={handleNewClientChange}
                              placeholder="Client full name"
                              className={calmField}
                              disabled={saving}
                            />
                          </div>

                          <div>
                            <Label className={strongLabel}>Company Name</Label>
                            <Input
                              name="company_name"
                              value={newClient.company_name}
                              onChange={handleNewClientChange}
                              placeholder="Client company / organisation"
                              className={calmField}
                              disabled={saving}
                            />
                          </div>

                          <div>
                            <Label className={`flex items-center gap-2 ${strongLabel}`}>
                              <Mail className="h-4 w-4 text-sky-700" /> Primary Email
                            </Label>
                            <Input
                              name="email"
                              value={newClient.email}
                              onChange={handleNewClientChange}
                              placeholder="client@email.com"
                              className={calmField}
                              disabled={saving}
                            />
                          </div>

                          <div>
                            <Label className={`flex items-center gap-2 ${strongLabel}`}>
                              <Phone className="h-4 w-4 text-sky-700" /> Primary Phone
                            </Label>
                            <Input
                              name="phone"
                              value={newClient.phone}
                              onChange={handleNewClientChange}
                              placeholder="Phone number"
                              className={calmField}
                              disabled={saving}
                            />
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                          <div className="mb-4 font-semibold text-slate-950">Billing / Invoice Details</div>

                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                              <Label className={strongLabel}>Invoicing Email</Label>
                              <Input
                                name="invoicing_email"
                                value={newClient.invoicing_email}
                                onChange={handleNewClientChange}
                                placeholder="accounts@client.com"
                                className={calmField}
                                disabled={saving}
                              />
                            </div>

                            <div>
                              <Label className={strongLabel}>Billing Contact Name</Label>
                              <Input
                                name="billing_contact_name"
                                value={newClient.billing_contact_name}
                                onChange={handleNewClientChange}
                                placeholder="Accounts contact"
                                className={calmField}
                                disabled={saving}
                              />
                            </div>

                            <div>
                              <Label className={strongLabel}>Billing Phone</Label>
                              <Input
                                name="billing_phone"
                                value={newClient.billing_phone}
                                onChange={handleNewClientChange}
                                placeholder="Billing phone number"
                                className={calmField}
                                disabled={saving}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                          <div className="mb-4 font-semibold text-slate-950">Billing Address</div>

                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="md:col-span-2">
                              <Label className={strongLabel}>Address Line 1</Label>
                              <Input
                                name="address_line_1"
                                value={newClient.address_line_1}
                                onChange={handleNewClientChange}
                                placeholder="Street address"
                                className={calmField}
                                disabled={saving}
                              />
                            </div>

                            <div className="md:col-span-2">
                              <Label className={strongLabel}>Address Line 2</Label>
                              <Input
                                name="address_line_2"
                                value={newClient.address_line_2}
                                onChange={handleNewClientChange}
                                placeholder="Building, suite, floor (optional)"
                                className={calmField}
                                disabled={saving}
                              />
                            </div>

                            <div>
                              <Label className={strongLabel}>City</Label>
                              <Input
                                name="city"
                                value={newClient.city}
                                onChange={handleNewClientChange}
                                placeholder="City"
                                className={calmField}
                                disabled={saving}
                              />
                            </div>

                            <div>
                              <Label className={strongLabel}>County / State</Label>
                              <Input
                                name="county"
                                value={newClient.county}
                                onChange={handleNewClientChange}
                                placeholder="County / State"
                                className={calmField}
                                disabled={saving}
                              />
                            </div>

                            <div>
                              <Label className={strongLabel}>Postcode</Label>
                              <Input
                                name="postcode"
                                value={newClient.postcode}
                                onChange={handleNewClientChange}
                                placeholder="Postcode / ZIP"
                                className={calmField}
                                disabled={saving}
                              />
                            </div>

                            <div>
                              <Label className={strongLabel}>Country</Label>
                              <Input
                                name="country"
                                value={newClient.country}
                                onChange={handleNewClientChange}
                                placeholder="Country"
                                className={calmField}
                                disabled={saving}
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <Label className={strongLabel}>Client Notes</Label>
                          <Textarea
                            name="notes"
                            value={newClient.notes}
                            onChange={handleNewClientChange}
                            placeholder="Communication notes, billing notes, VIP notes..."
                            className={calmArea}
                            disabled={saving}
                          />
                        </div>

                        <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
                          New client details will be sent with the event payload, including billing and address fields.
                          The event create/update backend should persist or resolve `client_id` correctly and return the
                          joined client data when loading an event for edit.
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="rounded-3xl border-slate-200 bg-white shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-slate-950">
                      <Search className="h-5 w-5 text-sky-700" /> Food & Beverage
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-5">
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_260px]">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                          <div>
                            <div className="font-semibold text-slate-950">Menu items</div>
                            <div className="mt-1 text-sm text-slate-700">
                              Open the menu picker to search and add dishes or drinks.
                            </div>
                          </div>

                          <Button
                            type="button"
                            onClick={() => setMenuPickerOpen(true)}
                            disabled={saving}
                            className="gap-2 rounded-2xl"
                          >
                            <UtensilsCrossed className="h-4 w-4" />
                            Menu Items
                          </Button>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-700">
                          <span>
                            Selected <span className="font-semibold text-slate-900">{lines.length}</span> lines
                          </span>
                          <span className="text-slate-300">•</span>
                          <span>
                            Portions <span className="font-semibold text-slate-900">{selectedCount}</span>
                          </span>
                          <span className="text-slate-300">•</span>
                          <span>
                            Subtotal{" "}
                            <span className="font-semibold text-slate-900">
                              {currency(selectedSubtotal)}
                            </span>
                          </span>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={bulkSetQtyToGuests}
                            disabled={saving || !num(formData.expected_guests) || !lines.length}
                            className="rounded-2xl"
                          >
                            Set qty = guests
                          </Button>

                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={bulkResetPricesFromCatalog}
                            disabled={saving || !lines.length}
                            className="rounded-2xl"
                          >
                            Reset prices
                          </Button>

                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={clearAllLines}
                            disabled={saving || !lines.length}
                            className="rounded-2xl"
                          >
                            Clear selected
                          </Button>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <Label className={strongLabel}>Room hire (optional)</Label>
                        <Input
                          inputMode="decimal"
                          value={formData.room_hire}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              room_hire: toMoneyInputString(e.target.value),
                            }))
                          }
                          className={`${calmField} mt-2`}
                          disabled={saving}
                        />
                        <div className="mt-3 text-xs text-slate-700">
                          Added into sales subtotal before tax and service.
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <SelectedLineTable
                        title="Food"
                        accent="emerald"
                        lines={linesFood}
                        saving={saving}
                        onDecrease={decQty}
                        onIncrease={incQty}
                        onUpdate={updateLine}
                        onRemove={removeLine}
                      />

                      <SelectedLineTable
                        title="Drinks"
                        accent="sky"
                        lines={linesDrink}
                        saving={saving}
                        onDecrease={decQty}
                        onIncrease={incQty}
                        onUpdate={updateLine}
                        onRemove={removeLine}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-3xl border-slate-200 bg-white shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-slate-950">
                      <Briefcase className="h-5 w-5 text-sky-700" /> Staffing Forecast by Cost Centre
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {!costCentres.length ? (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        Cost centres are not available yet for this page. Once cost centre rows are loaded, you will be
                        able to choose one here and save forecast staffing lines.
                      </div>
                    ) : null}

                    <div className="flex flex-col gap-3 md:flex-row md:items-end">
                      <div className="flex-1">
                        <Label className={strongLabel}>Add cost centre</Label>
                        <Select
                          value={selectedStaffingCostCentre}
                          onValueChange={(value) => {
                            setSelectedStaffingCostCentre(value);
                            addStaffingLine(value);
                          }}
                          disabled={saving || !availableCostCentres.length}
                        >
                          <SelectTrigger className={calmSelectTrigger}>
                            <SelectValue
                              placeholder={
                                availableCostCentres.length
                                  ? "Select a cost centre to add"
                                  : costCentres.length
                                  ? "All available cost centres already added"
                                  : "No cost centres available"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {availableCostCentres.map((c) => (
                              <SelectItem key={c.cost_centre_id} value={String(c.cost_centre_id)}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={clearAllStaffingLines}
                          disabled={saving || !staffingLines.length}
                          className="rounded-2xl"
                        >
                          Clear staffing
                        </Button>
                      </div>
                    </div>

                    {staffingLines.length ? (
                      <div className="space-y-3">
                        {staffingLines.map((row) => {
                          const meta =
                            costCentres.find(
                              (c) => Number(c.cost_centre_id) === Number(row.cost_centre_id)
                            ) || null;

                          return (
                            <div
                              key={row.cost_centre_id}
                              className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                            >
                              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                <div>
                                  <div className="font-medium text-slate-950">
                                    {meta?.label || `Cost Centre ${row.cost_centre_id}`}
                                  </div>
                                  <div className="text-xs text-slate-700">
                                    Forecast staffing amount for this cost centre
                                  </div>
                                </div>

                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="rounded-2xl hover:bg-red-50"
                                  onClick={() => removeStaffingLine(row.cost_centre_id)}
                                  disabled={saving}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" /> Remove
                                </Button>
                              </div>

                              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div>
                                  <Label className={`flex items-center gap-2 ${strongLabel}`}>
                                    <Wallet className="h-4 w-4 text-sky-700" /> Forecast Amount
                                  </Label>
                                  <Input
                                    inputMode="decimal"
                                    value={row.amount}
                                    onChange={(e) =>
                                      updateStaffingLine(row.cost_centre_id, {
                                        amount: toMoneyInputString(e.target.value),
                                      })
                                    }
                                    className={calmField}
                                    disabled={saving}
                                  />
                                </div>

                                <div>
                                  <Label className={strongLabel}>Notes (optional)</Label>
                                  <Input
                                    value={row.notes || ""}
                                    onChange={(e) =>
                                      updateStaffingLine(row.cost_centre_id, {
                                        notes: e.target.value,
                                      })
                                    }
                                    placeholder="e.g. agency staff / chefs / service team"
                                    className={calmField}
                                    disabled={saving}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        <div className="flex justify-end text-sm text-slate-800">
                          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                            Forecast Staffing Total:{" "}
                            <span className="font-semibold text-slate-950">
                              {currency(totals.staffingForecast)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
                        No staffing forecast lines added yet. Add one or more cost centres above.
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    disabled={saving}
                    className="gap-2 rounded-2xl px-5"
                  >
                    Next: Quotation & Profit <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Card className="rounded-3xl border-slate-200 bg-white shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-slate-950">Quotation & Profit</CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
                      <div className="space-y-4">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                              <Label className={`flex items-center gap-2 ${strongLabel}`}>
                                <DollarSign className="h-4 w-4 text-sky-700" /> Discount (absolute)
                              </Label>
                              <Input
                                inputMode="decimal"
                                value={formData.discount}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    discount: toMoneyInputString(e.target.value),
                                  }))
                                }
                                className={`${calmField} mt-1`}
                                disabled={saving}
                              />
                            </div>

                            <div>
                              <Label className={`flex items-center gap-2 ${strongLabel}`}>
                                <Percent className="h-4 w-4 text-sky-700" /> Tax %
                              </Label>
                              <Input
                                inputMode="decimal"
                                value={formData.tax_rate}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    tax_rate: toMoneyInputString(e.target.value),
                                  }))
                                }
                                className={`${calmField} mt-1`}
                                disabled={saving}
                              />
                            </div>

                            <div>
                              <Label className={`flex items-center gap-2 ${strongLabel}`}>
                                <Percent className="h-4 w-4 text-sky-700" /> Service %
                              </Label>
                              <Input
                                inputMode="decimal"
                                value={formData.service_rate}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    service_rate: toMoneyInputString(e.target.value),
                                  }))
                                }
                                className={`${calmField} mt-1`}
                                disabled={saving}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="font-semibold text-slate-950">Commercial notes</div>
                          <div className="mt-1 text-sm text-slate-700">
                            Review the calculations on the right, then save the event.
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="font-semibold text-slate-950">Client summary</div>
                          <div className="mt-2 text-sm text-slate-700">
                            {clientMode === "existing" ? (
                              selectedClient ? (
                                <>
                                  <div className="font-medium text-slate-900">
                                    {selectedClient.client_name || "Unnamed client"}
                                  </div>
                                  <div>{selectedClient.email || "No primary email"}</div>
                                  <div>{selectedClient.phone || "No primary phone"}</div>
                                  <div>{selectedClient.company_name || "No company name"}</div>
                                  <div>{selectedClient.invoicing_email || "No invoicing email"}</div>
                                  <div>{formatClientAddress(selectedClient) || "No billing address"}</div>
                                </>
                              ) : (
                                <div>No client selected.</div>
                              )
                            ) : hasInlineClientValues ? (
                              <>
                                <div className="font-medium text-slate-900">
                                  {newClient.client_name || "Unnamed client"}
                                </div>
                                <div>{newClient.email || "No primary email"}</div>
                                <div>{newClient.phone || "No primary phone"}</div>
                                <div>{newClient.company_name || "No company name"}</div>
                                <div>{newClient.invoicing_email || "No invoicing email"}</div>
                                <div>{formatClientAddress(newClient) || "No billing address"}</div>
                              </>
                            ) : (
                              <div>No client entered.</div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="font-semibold text-slate-950">Commercial summary</div>
                          <div className="text-xs text-slate-700">
                            Live calculations from current selections
                          </div>
                        </div>

                        <div className="px-4 py-4">
                          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-slate-700">Food sales</span>
                                <span className="font-medium text-slate-950">{currency(totals.foodSales)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-700">Drink sales</span>
                                <span className="font-medium text-slate-950">{currency(totals.drinkSales)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-700">Room hire</span>
                                <span className="font-medium text-slate-950">{currency(formData.room_hire)}</span>
                              </div>
                              <div className="flex justify-between pt-1 text-sm text-slate-700">
                                <span>Tax</span>
                                <span>{currency(totals.tax)}</span>
                              </div>
                              <div className="flex justify-between text-sm text-slate-700">
                                <span>Service</span>
                                <span>{currency(totals.service)}</span>
                              </div>
                              {num(formData.discount) ? (
                                <div className="flex justify-between text-sm text-slate-700">
                                  <span>Discount</span>
                                  <span>-{currency(formData.discount)}</span>
                                </div>
                              ) : null}

                              <div className="my-3 border-t border-slate-200" />
                              <div className="flex justify-between text-lg font-semibold">
                                <span className="text-slate-950">Client total</span>
                                <span className="text-slate-950">{currency(totals.gross)}</span>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <div className="flex justify-between text-sm text-slate-700">
                                <span>COGS (food)</span>
                                <span>{currency(totals.foodCost)}</span>
                              </div>
                              <div className="flex justify-between text-sm text-slate-700">
                                <span>COGS (drink)</span>
                                <span>{currency(totals.drinkCost)}</span>
                              </div>
                              <div className="flex justify-between text-sm text-slate-700">
                                <span>Forecast staffing</span>
                                <span>{currency(totals.staffingForecast)}</span>
                              </div>

                              <div className="my-3 border-t border-slate-200" />
                              <div className="flex justify-between font-medium">
                                <span className="text-slate-950">Gross Profit</span>
                                <span className="text-slate-950">{currency(totals.gp)}</span>
                              </div>
                              <div className="flex justify-between text-sm text-slate-700">
                                <span>Margin on sales</span>
                                <span>{totals.margin.toFixed(1)}%</span>
                              </div>

                              <div className="my-3 border-t border-slate-200" />
                              <div className="flex justify-between font-medium">
                                <span className="text-slate-950">Net after staffing</span>
                                <span className="text-slate-950">{currency(totals.netAfterStaffing)}</span>
                              </div>
                              <div className="flex justify-between text-sm text-slate-700">
                                <span>Net margin after staffing</span>
                                <span>{totals.netMarginAfterStaffing.toFixed(1)}%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                        <div className="font-semibold text-slate-950">Commercial breakdown table</div>
                        <div className="text-xs text-slate-700">
                          Review before saving. You can go back and edit anything.
                        </div>
                      </div>

                      <div className="overflow-auto">
                        <table className="min-w-full divide-y divide-slate-200">
                          <thead className="bg-slate-50">
                            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                              <th className="px-4 py-3">Metric</th>
                              <th className="px-4 py-3 text-right">Value</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200 text-sm">
                            <tr>
                              <td className="px-4 py-3 text-slate-800">Food sales</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                {currency(totals.foodSales)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 text-slate-800">Drink sales</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                {currency(totals.drinkSales)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 text-slate-800">Room hire</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                {currency(formData.room_hire)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 text-slate-800">Tax</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                {currency(totals.tax)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 text-slate-800">Service</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                {currency(totals.service)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 text-slate-800">Discount</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                -{currency(formData.discount)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 font-semibold text-slate-950">Client total</td>
                              <td className="px-4 py-3 text-right font-semibold text-slate-950">
                                {currency(totals.gross)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 text-slate-800">COGS (food)</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                {currency(totals.foodCost)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 text-slate-800">COGS (drink)</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                {currency(totals.drinkCost)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 text-slate-800">Forecast staffing</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                {currency(totals.staffingForecast)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 font-semibold text-slate-950">Gross Profit</td>
                              <td className="px-4 py-3 text-right font-semibold text-slate-950">
                                {currency(totals.gp)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 text-slate-800">Margin on sales</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                {totals.margin.toFixed(1)}%
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 font-semibold text-slate-950">Net after staffing</td>
                              <td className="px-4 py-3 text-right font-semibold text-slate-950">
                                {currency(totals.netAfterStaffing)}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-3 text-slate-800">Net margin after staffing</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-950">
                                {totals.netMarginAfterStaffing.toFixed(1)}%
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handlePrevStep}
                    disabled={saving}
                    className="gap-2 rounded-2xl"
                  >
                    <ChevronLeft className="h-4 w-4" /> Back to Event Build
                  </Button>

                  <Button type="submit" disabled={saving} className="gap-2 rounded-2xl px-5 text-base">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving ? "Saving…" : isEdit ? "Update Event" : "Create Event"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
}